import { DomainAvailabilityStatus } from '../../typing';
import { parseDomain } from '../../utils';
import { PremiumMyDomainsApiClient } from '../ApiClients/ProtoServicesApiClients';
import { ConnectionType } from '@wix/ambassador-premium-domains-v1-connected-domain/types';
import { ConnectOrBindLogger } from '../Loggers/DataLogger';
import { ManagementTypeEnumManagementType } from '@wix/ambassador-domains-userdomain-v1-user-domain/types';
import { getPremiumEssentialsSafe } from '@wix/premium-fed-sdk/@premium-essentials';
import { Specs } from '../../constants/Specs';
import { AssignmentType } from '@wix/ambassador-premium-domains-v1-domain-assignment/types';

/**
 * Assigns (or reassigns) a domain to a site. Picks between the legacy
 * `bindDomain` API and the Nile domain-assignment API based on the
 * `DOMAIN_ASSIGNMENT_FED_MIGRATION` experiment.
 *
 * Legacy `bindDomain` is an upsert (create-or-update). Nile splits that into
 * `reassignDomain` (update) and `createDomainAssignment` (create), so we try
 * the reassign first and fall back to create on 404 — letting Nile decide
 * which path the domain is on.
 */
const assignDomainToSite = async _ref => {
  let httpClient = _ref.httpClient,
    domainName = _ref.domainName,
    siteId = _ref.siteId;
  const _ref2 = getPremiumEssentialsSafe() ?? {},
    experiments = _ref2.experiments;
  const useNile = experiments == null ? void 0 : experiments[Specs.DOMAIN_ASSIGNMENT_FED_MIGRATION];
  if (useNile) {
    const _await$import$Domains = (await import('../ApiClients/DomainsApiClient')).DomainsApiClient.queries({
        httpClient
      }),
      upsertDomainAssignment = _await$import$Domains.upsertDomainAssignment;

    // AssignmentType.REDIRECT will act as PRIMARY for new assignments, and as SECONDARY for existing ones, so it works for both create and reassign without us having to check which case we're in
    await upsertDomainAssignment({
      domainAssignment: {
        domain: domainName,
        siteInfo: {
          id: siteId,
          assignmentType: AssignmentType.REDIRECT
        }
      }
    });
    return;
  }
  const _PremiumMyDomainsApiC = PremiumMyDomainsApiClient.queries({
      httpClient
    }),
    bindDomain = _PremiumMyDomainsApiC.bindDomain;
  await bindDomain({
    domainName,
    siteId
  });
};

/// call userDomains.get
/// if exists -> myDomains.bind
/// if exception:permission_denied -> exception (belongs to another user)
/// if exception:not_found -> domainSearch.checkDomainAvailability ->
/// if available -> exception (requires registration)
/// if taken -> create-connected-domain connectionType by requestParam (

// subdomain:
// call userDomains.get
// if exists -> myDomains.bind
/// if exception:permission_denied -> exception (belongs to another user)
/// if exception:not_found -> create-connected-domain connectionType: undefined

export const connectOrBindDomain = async _ref3 => {
  let httpClient = _ref3.httpClient,
    _ref3$request = _ref3.request,
    domain = _ref3$request.domain,
    msid = _ref3$request.msid,
    connectionType = _ref3$request.connectionType,
    connectedByEntri = _ref3$request.connectedByEntri;
  const parsedDomain = domain.asciiDomain ? domain : parseDomain(domain);
  const rootDomain = parsedDomain.asciiDomain;
  const fullAsciiDomain = parsedDomain.fullAsciiDomain;
  const isSubdomain = !!parsedDomain.subdomain;
  ConnectOrBindLogger.onConnectOrBindStart == null || ConnectOrBindLogger.onConnectOrBindStart({
    domainName: parsedDomain == null ? void 0 : parsedDomain.formattedInput,
    connectionType,
    msid
  });
  try {
    if (isSubdomain) {
      return connectOrBindSubdomain({
        httpClient,
        subdomain: fullAsciiDomain,
        rootDomain,
        msid,
        connectedByEntri
      });
    } else {
      return connectOrBindRootDomain({
        httpClient,
        rootDomain,
        msid,
        connectionType,
        connectedByEntri
      });
    }
  } catch (e) {
    ConnectOrBindLogger.onConnectOrBindError == null || ConnectOrBindLogger.onConnectOrBindError({
      e: e,
      domainName: parsedDomain == null ? void 0 : parsedDomain.formattedInput,
      connectionType,
      msid
    });
    throw e;
  }
};
async function connectOrBindRootDomain(_ref4) {
  var _connectedDomain$site;
  let httpClient = _ref4.httpClient,
    rootDomain = _ref4.rootDomain,
    msid = _ref4.msid,
    connectionType = _ref4.connectionType,
    connectedByEntri = _ref4.connectedByEntri;
  const _await$import$Domains2 = (await import('../ApiClients/DomainsApiClient')).DomainsApiClient.queries({
      httpClient
    }),
    createConnectedDomain = _await$import$Domains2.createConnectedDomain,
    deleteConnectedDomain = _await$import$Domains2.deleteConnectedDomain,
    checkDomainAvailability = _await$import$Domains2.checkDomainAvailability,
    forceDomainCheck = _await$import$Domains2.forceDomainCheck,
    userDomain = _await$import$Domains2.userDomain;
  const _await$userDomain = await userDomain({
      domain: rootDomain,
      withSiteData: true
    }, {
      cacheTime: 0
    }),
    error = _await$userDomain.error,
    domain = _await$userDomain.domain,
    site = _await$userDomain.site;
  const isRootDomainInAccount = !error;
  const rootDomainBelongsToAnotherWixAccount = error === 'belongsToAnotherAccount';
  ConnectOrBindLogger.onConnectOrBindRootDomainStart == null || ConnectOrBindLogger.onConnectOrBindRootDomainStart({
    domainName: rootDomain,
    connectionType,
    msid
  });
  ConnectOrBindLogger.onConnectOrBindOwnerCheck == null || ConnectOrBindLogger.onConnectOrBindOwnerCheck({
    domainName: rootDomain,
    connectionType,
    msid,
    isRootDomainInAccount,
    connectedDomain: domain
  });
  if (isRootDomainInAccount) {
    const isAlreadyConnectedToSite = (site == null ? void 0 : site.siteId) === msid;
    const siteId = msid ?? (site == null ? void 0 : site.siteId);
    const managementType = connectionType === 'dns' ? ManagementTypeEnumManagementType.DNS : ManagementTypeEnumManagementType.POINTING;
    if ((domain == null ? void 0 : domain.managementType) !== managementType && connectionType) {
      ConnectOrBindLogger.onConnectOrBindUpdateStart == null || ConnectOrBindLogger.onConnectOrBindUpdateStart({
        domainName: rootDomain,
        connectionType,
        msid
      });
      await deleteConnectedDomain({
        connectedDomainId: rootDomain
      });
      await createConnectedDomain({
        connectedByEntri: !!connectedByEntri,
        connectedDomain: {
          domain: rootDomain,
          connectionType: connectionType === 'pointing' ? ConnectionType.POINTING : ConnectionType.NAMESERVERS
        }
      });
      if (siteId) {
        await assignDomainToSite({
          httpClient,
          domainName: rootDomain,
          siteId
        });
      }
    }
    if (!isAlreadyConnectedToSite && (site == null ? void 0 : site.siteId) !== msid && msid) {
      await assignDomainToSite({
        httpClient,
        domainName: rootDomain,
        siteId: msid
      });
    }
    if ((domain == null ? void 0 : domain.managementType) === managementType) {
      await forceDomainCheck({
        domainName: rootDomain
      });
    }
    return {
      domainName: rootDomain,
      msid: msid ?? (site == null ? void 0 : site.siteId),
      prevSiteId: site == null ? void 0 : site.siteId
    };
  }
  if (rootDomainBelongsToAnotherWixAccount) {
    return {
      error: DomainAvailabilityStatus.DOMAIN_IN_ANOTHER_WIX_ACCOUNT
    };
  }
  try {
    var _availability$availab;
    const availability = await checkDomainAvailability({
      domain: rootDomain
    });
    const isRootDomainAvailable = (_availability$availab = availability.availability) == null ? void 0 : _availability$availab.available;
    if (isRootDomainAvailable) {
      return {
        error: DomainAvailabilityStatus.AVAILABLE_FOR_PURCHASE
      };
    }
  } catch (e) {
    var _e$response;
    // Don't delete this part it's for com.tr, gets 400 error on availabilty check
    const statusCode = (e == null || (_e$response = e.response) == null ? void 0 : _e$response.status) ?? (e == null ? void 0 : e.status);
    if (statusCode !== 400) {
      throw e;
    }
  }
  ConnectOrBindLogger.onConnectOrBindCreateStart == null || ConnectOrBindLogger.onConnectOrBindCreateStart({
    domainName: rootDomain,
    connectionType,
    msid
  });
  const _await$createConnecte = await createConnectedDomain({
      connectedByEntri: !!connectedByEntri,
      connectedDomain: {
        domain: rootDomain,
        connectionType: connectionType === 'pointing' ? ConnectionType.POINTING : ConnectionType.NAMESERVERS,
        siteInfo: {
          id: msid
        }
      }
    }),
    connectedDomain = _await$createConnecte.connectedDomain;
  if (!(connectedDomain != null && (_connectedDomain$site = connectedDomain.siteInfo) != null && _connectedDomain$site.id)) {
    await assignDomainToSite({
      httpClient,
      domainName: rootDomain,
      siteId: msid
    });
  }
  return {
    domainName: rootDomain,
    msid,
    prevSiteId: site == null ? void 0 : site.siteId
  };
}
async function connectOrBindSubdomain(_ref5) {
  let httpClient = _ref5.httpClient,
    subdomain = _ref5.subdomain,
    rootDomain = _ref5.rootDomain,
    msid = _ref5.msid,
    connectedByEntri = _ref5.connectedByEntri;
  const _await$import$Domains3 = (await import('../ApiClients/DomainsApiClient')).DomainsApiClient.queries({
      httpClient
    }),
    getConnectedDomain = _await$import$Domains3.getConnectedDomain,
    userDomain = _await$import$Domains3.userDomain;
  ConnectOrBindLogger.onConnectOrBindSubDomainStart == null || ConnectOrBindLogger.onConnectOrBindSubDomainStart({
    domainName: rootDomain,
    msid
  });
  const _await$userDomain2 = await userDomain({
      domain: subdomain
    }),
    error = _await$userDomain2.error;
  const isSubdomainInAccount = !error;
  if (isSubdomainInAccount) {
    await assignDomainToSite({
      httpClient,
      domainName: subdomain,
      siteId: msid
    });
    return {
      domainName: subdomain,
      msid
    };
  }
  if (error === 'belongsToAnotherAccount') {
    return {
      error: DomainAvailabilityStatus.DOMAIN_IN_ANOTHER_WIX_ACCOUNT
    };
  }
  ConnectOrBindLogger.onConnectOrBindSubdomainCreateStart == null || ConnectOrBindLogger.onConnectOrBindSubdomainCreateStart({
    domainName: subdomain,
    msid
  });
  try {
    const _await$getConnectedDo = await getConnectedDomain({
        connectedDomainId: rootDomain
      }),
      connectedDomain = _await$getConnectedDo.connectedDomain;
    if ((connectedDomain == null ? void 0 : connectedDomain.connectionType) === ConnectionType.POINTING) {
      const connectionTypeError = 'rootDomainIsConnectedByPointing';
      return {
        error: connectionTypeError
      };
    }
    return await createAndBindSubdomain({
      httpClient,
      msid,
      subdomain,
      connectedByEntri
    });
  } catch (e) {
    var _e$response2;
    const statusCode = (e == null || (_e$response2 = e.response) == null ? void 0 : _e$response2.status) ?? (e == null ? void 0 : e.status);
    switch (statusCode) {
      case 404:
        {
          return await createAndBindSubdomain({
            httpClient,
            msid,
            subdomain,
            connectedByEntri
          });
        }
      default:
        {
          throw e;
        }
    }
  }
}
const createAndBindSubdomain = async _ref6 => {
  var _connectedDomainData$;
  let httpClient = _ref6.httpClient,
    subdomain = _ref6.subdomain,
    msid = _ref6.msid,
    connectedByEntri = _ref6.connectedByEntri;
  const _await$import$Domains4 = (await import('../ApiClients/DomainsApiClient')).DomainsApiClient.queries({
      httpClient
    }),
    createConnectedDomain = _await$import$Domains4.createConnectedDomain;
  const _await$createConnecte2 = await createConnectedDomain({
      connectedByEntri: !!connectedByEntri,
      connectedDomain: {
        domain: subdomain,
        siteInfo: {
          id: msid
        }
      }
    }),
    connectedDomainData = _await$createConnecte2.connectedDomain;
  if (!(connectedDomainData != null && (_connectedDomainData$ = connectedDomainData.siteInfo) != null && _connectedDomainData$.id)) {
    await assignDomainToSite({
      httpClient,
      domainName: subdomain,
      siteId: msid
    });
  }
  return {
    domainName: subdomain,
    msid
  };
};
//# sourceMappingURL=connectOrBindDomain.js.map